import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationSerializer from 'lumen/serializers/application';
import { v4 as uuidv4 } from 'uuid';

type BreakdownValuePayload = {
  benchmark?: Record<string, number> | null;
  extract_date?: string;
  id: string;
  meta?: Record<string, string>;
  parent_id?: string;
  segmentation_id: string;
  segmentation_value: string;
  segmentation_value_id: string;
  segmentation_value_type: string;
  unfiltered_id?: string;
  value: Record<string, number>;
};

type HistoricalRequestPayload = Record<
  string,
  Record<string, BreakdownValuePayload[]>
>;

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class ParityInsightHistoricalDataSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    items: { embedded: 'always' },
  };

  extractId(): string {
    return uuidv4();
  }

  modelNameFromPayloadKey(): string {
    // Required to avoid singularization to historical-datum in queryRecord
    return 'parity/insight/historical-data';
  }

  normalizeQueryRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: HistoricalRequestPayload,
    id: string | number,
    requestType: string,
  ): ReturnType<ApplicationSerializer['normalizeQueryRecordResponse']> {
    const items: BreakdownValuePayload[] = [];

    Object.entries(payload).forEach(([segmentationKey, datedRows]) => {
      Object.entries(datedRows ?? {}).forEach(([date, rows]) => {
        // Preserve empty dates for interpolation in evolution charts
        if (!rows?.length) {
          items.push({
            extract_date: date,
            id: `${segmentationKey}__${date}`,
            segmentation_id: segmentationKey,
            benchmark: null,
            value: { win: 0, meet: 0, loss: 0, subscriptions: 0 },
            segmentation_value: '',
            segmentation_value_id: '',
            segmentation_value_type: '',
            meta: {},
            parent_id: '',
            unfiltered_id: '',
          });
        } else {
          rows.forEach((row) => {
            items.push({
              ...row,
              benchmark: row.benchmark ?? null,
              id: `${row.id}__${date}`,
              extract_date: date,
            });
          });
        }
      });
    });

    const restPayload = {
      [primaryModelClass.modelName]: { items },
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.normalizeQueryRecordResponse(
      store,
      primaryModelClass,
      restPayload,
      id,
      requestType,
    );
  }
}
